import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Send, Loader2, TrendingUp, Lightbulb, RotateCcw, ChevronRight } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { toast } from 'sonner';

const PALETTE = ['#A5D62C', '#1B9CEA', '#F59E0B', '#A855F7', '#EC4899', '#14B8A6', '#3B82F6', '#10B981', '#EF4444', '#64748B'];
const TOOLTIP_STYLE = { background: '#0F1117', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 11, color: '#fff' };

const formatValue = (v, currency) => {
  if (v == null) return '—';
  if (typeof v !== 'number') return String(v);
  if (currency === 'MXN' || currency === 'USD') return `$${v.toLocaleString('es-MX')}`;
  if (currency === '%') return `${v}%`;
  return v.toLocaleString('es-MX');
};

// Renderer dinámico de gráficos según el `type` que devuelve el LLM
function DynamicChart({ chart }) {
  const { type, data = [], config = {} } = chart;
  const xKey = config.xKey || 'name';
  const yKey = config.yKey || 'value';
  const yKeys = Array.isArray(config.yKeys) ? config.yKeys : [yKey];
  const valueKey = config.valueKey || yKey;
  const categoryKey = config.categoryKey || xKey;
  const currency = config.currency;
  const tickFormatter = currency === 'MXN' || currency === 'USD'
    ? v => `$${(v / 1000).toFixed(0)}k`
    : currency === '%' ? v => `${v}%` : undefined;

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-xs">
        Sin datos disponibles para este gráfico.
      </div>
    );
  }

  switch (type) {
    case 'kpi_card': {
      const item = data[0] || {};
      const label = item.label || config.label || 'KPI';
      const value = item.value ?? item[yKey] ?? item.kpi ?? '—';
      const sub = item.sub || item.subtitle || '';
      return (
        <div className="p-6 rounded-xl bg-gradient-to-br from-cyan-500/10 to-violet-500/10 border border-cyan-500/20 text-center">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">{label}</p>
          <p className="text-4xl font-bold font-heading text-cyan-300">{typeof value === 'number' ? formatValue(value, currency) : value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-2">{sub}</p>}
        </div>
      );
    }

    case 'bar':
      return (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey={xKey} tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} tickFormatter={tickFormatter} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={v => [typeof v === 'number' ? formatValue(v, currency) : v, '']} />
            {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
            {yKeys.map((k, i) => (
              <Bar key={k} dataKey={k} fill={PALETTE[i % PALETTE.length]} radius={[4, 4, 0, 0]}>
                {yKeys.length === 1 && data.map((_, idx) => <Cell key={idx} fill={PALETTE[idx % PALETTE.length]} />)}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      );

    case 'line':
      return (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey={xKey} tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} tickFormatter={tickFormatter} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={v => [typeof v === 'number' ? formatValue(v, currency) : v, '']} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {yKeys.map((k, i) => (
              <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );

    case 'area':
      return (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey={xKey} tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} tickFormatter={tickFormatter} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={v => [typeof v === 'number' ? formatValue(v, currency) : v, '']} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {yKeys.map((k, i) => (
              <Area key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.3} strokeWidth={2} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );

    case 'pie':
      return (
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={data} dataKey={valueKey} nameKey={categoryKey} innerRadius={50} outerRadius={90} paddingAngle={2} label={{ fill: '#cbd5e1', fontSize: 10 }}>
              {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={v => [typeof v === 'number' ? formatValue(v, currency) : v, '']} />
          </PieChart>
        </ResponsiveContainer>
      );

    case 'scatter':
      return (
        <ResponsiveContainer width="100%" height={260}>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey={xKey} type="number" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} name={xKey} />
            <YAxis dataKey={yKey} type="number" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} name={yKey} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={TOOLTIP_STYLE} />
            <Scatter data={data} fill="#A855F7" />
          </ScatterChart>
        </ResponsiveContainer>
      );

    case 'waterfall': {
      // Waterfall renderizado como bar chart con colores diferenciados por signo
      const wfData = data.map(d => ({
        ...d,
        _color: (d[valueKey] || 0) >= 0 ? '#10B981' : '#EF4444',
      }));
      return (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={wfData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey={xKey} tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} tickFormatter={tickFormatter} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={v => [typeof v === 'number' ? formatValue(v, currency) : v, '']} />
            <Bar dataKey={valueKey} radius={[4, 4, 0, 0]}>
              {wfData.map((d, i) => <Cell key={i} fill={d._color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    case 'table':
      return (
        <div className="overflow-x-auto max-h-72">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-white/5 sticky top-0 bg-[#0F1117]">
                {Object.keys(data[0] || {}).map(k => (
                  <th key={k} className="py-2 px-3 font-normal">{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className="border-b border-white/[0.02]">
                  {Object.entries(row).map(([k, v]) => (
                    <td key={k} className="py-2 px-3 text-white/80">
                      {typeof v === 'number' ? formatValue(v, currency) : String(v)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return (
        <div className="text-xs text-muted-foreground italic">
          Tipo de gráfico no soportado: {type}
        </div>
      );
  }
}

export default function ExecutiveConsole() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    api.get('/dashboard/v2/exec-suggestions')
      .then(r => setSuggestions(r.data?.suggestions || []))
      .catch(() => setSuggestions([]));
  }, []);

  const ask = async (q) => {
    const finalQuery = (q || query || '').trim();
    if (!finalQuery) {
      toast.error('Escribe una pregunta primero');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post('/dashboard/v2/exec-query', { query: finalQuery, temperature: 0.4 }, { timeout: 90000 });
      setResult(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'El asistente no pudo responder. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setQuery('');
    setResult(null);
  };

  return (
    <div className="space-y-4" data-testid="executive-console">
      {/* Input principal */}
      <Card className="bg-gradient-to-br from-violet-500/10 via-cyan-500/5 to-transparent border-violet-500/20">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-violet-300" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white font-heading">Consulta ejecutiva inteligente</p>
              <p className="text-[10px] text-muted-foreground">Tu asistente IA con acceso total a la data del negocio</p>
            </div>
          </div>

          <Textarea
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Escribe tu consulta ejecutiva… (ej: ¿Cuál es el valor de mi empresa? · ¿Cómo está afectando el churn al flujo de caja? · ¿Quién es el vendedor más débil y proyecta sus ventas futuras?)"
            className="min-h-[88px] bg-black/40 border-white/10 text-sm focus:border-violet-400/50"
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) ask(); }}
            data-testid="exec-query-input"
          />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1.5">
              {suggestions.slice(0, 4).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => ask(s)}
                  disabled={loading}
                  className="text-[10px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-muted-foreground hover:text-white hover:border-cyan-400/30 hover:bg-cyan-400/5 transition-colors disabled:opacity-40"
                  data-testid={`exec-suggestion-${s.slice(0, 20)}`}
                >
                  {s.length > 45 ? s.slice(0, 45) + '…' : s}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {result && (
                <Button variant="ghost" size="sm" onClick={reset} className="text-xs text-muted-foreground hover:text-white" data-testid="exec-reset">
                  <RotateCcw className="w-3 h-3 mr-1" /> Nueva consulta
                </Button>
              )}
              <Button onClick={() => ask()} disabled={loading || !query.trim()} className="bg-violet-500 hover:bg-violet-600 text-white font-semibold text-xs h-8" data-testid="exec-submit">
                {loading ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analizando…</>
                ) : (
                  <>Consultar <Send className="w-3.5 h-3.5 ml-1.5" /></>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading skeleton */}
      {loading && (
        <Card className="bg-card/40 border-white/5" data-testid="exec-loading">
          <CardContent className="p-8 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
            <p className="text-sm text-white font-medium">El asistente está analizando tu data…</p>
            <p className="text-[10px] text-muted-foreground">Esto puede tomar 10-30 segundos.</p>
          </CardContent>
        </Card>
      )}

      {/* Resultado */}
      {result && !loading && (
        <div className="space-y-4" data-testid="exec-result">
          {/* Resumen ejecutivo + headline KPI */}
          <Card className="bg-gradient-to-br from-cyan-500/10 via-emerald-500/5 to-transparent border-cyan-500/20" data-testid="exec-summary">
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                {result.headline_kpi && (result.headline_kpi.value !== '—') && (
                  <div className="md:w-1/3 p-4 rounded-xl bg-black/30 border border-cyan-400/20 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{result.headline_kpi.label}</p>
                    <p className="text-3xl font-bold font-heading text-cyan-300 leading-none">{result.headline_kpi.value}</p>
                    {result.headline_kpi.delta_label && (
                      <p className="text-[10px] text-muted-foreground mt-2">{result.headline_kpi.delta_label}</p>
                    )}
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-cyan-300" />
                    <p className="text-xs font-semibold text-white uppercase tracking-wider">Resumen ejecutivo</p>
                    {result.intent && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300">
                        {result.intent}
                      </span>
                    )}
                    {result.cached && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300">caché</span>
                    )}
                  </div>
                  <p className="text-xs text-white/80 leading-relaxed whitespace-pre-line">{result.summary}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Gráficos dinámicos */}
          {result.charts && result.charts.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="exec-charts">
              {result.charts.map((chart, i) => (
                <Card key={i} className="bg-card/50 backdrop-blur-md border-white/5" data-testid={`exec-chart-${i}`}>
                  <CardContent className="p-4">
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-heading text-white font-semibold">{chart.title}</p>
                      </div>
                      <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/5 text-muted-foreground">{chart.type}</span>
                    </div>
                    <DynamicChart chart={chart} />
                    {chart.interpretation && (
                      <div className="mt-3 p-2.5 rounded-lg bg-violet-500/5 border border-violet-500/15">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-3.5 h-3.5 text-violet-300 flex-shrink-0 mt-0.5" />
                          <p className="text-[11px] text-white/85 italic leading-relaxed">{chart.interpretation}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Recomendaciones */}
          {result.recommendations && result.recommendations.length > 0 && (
            <Card className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent border-amber-500/20" data-testid="exec-recommendations">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="w-4 h-4 text-amber-300" />
                  <p className="text-xs font-semibold text-white uppercase tracking-wider">Sugerencias inteligentes</p>
                </div>
                <div className="space-y-2">
                  {result.recommendations.map((rec, i) => (
                    <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-black/20 border border-white/5 hover:border-amber-400/20 transition-colors" data-testid={`exec-rec-${i}`}>
                      <ChevronRight className="w-3.5 h-3.5 text-amber-300 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-white/85 leading-relaxed">{rec}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
